import React, { useEffect, useState } from 'react';
import IconClose from '~icons/mdi/close';
import {
  Stack,
  Button,
  Dialog,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  TextField,
  Tooltip
} from '@mui/material';
import { BugData, BugType, Centered } from 'its-pages';
import { APP_CFG } from '../appcfg';
import UAParser from 'ua-parser-js';
import { useAppSelector } from '../redux/hooks';
import { getCurrentPageContent } from '../redux/selectors/progression';
import IconFlag from '~icons/mdi/flag-outline'
import IconButton from '@mui/material/IconButton';
import styled from '@emotion/styled';

const stackProps: SxProps = {
  width: '80cqw',
  maxWidth: '640px',
  gap: 2,
  padding: '1em'
};

const emptyBugData: BugData = {
  issueDescription: '',
  platformInfo: new UAParser().getResult()
};

const CloseButton = styled(Button)`
  comment: 'Close Button';
  position: absolute;
  top: 0;
  right: 0;
  flex: 0 0 auto;
  display: flex;
  z-index: 200;
  flex-flow: row nowrap;
  align-self: flex-end;
  height: 30px;
  color: black;
  background-color: var(--Blue);
`;

const StyledForm = styled.form`
  container: auto;
`;

export const ReportBug = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bugData, setBugData] = useState<BugData>(emptyBugData);
  const testBody = false;//APP_CFG.testApp;

  const pageData = useAppSelector(getCurrentPageContent)?.pageData;

  useEffect(() => {
    const bugData = pageData?.bugData || emptyBugData;
    setBugData(bugData);
  }, [pageData]);

  const updateBugData = (bugData: BugData) => {
    if (!testBody) {
      setBugData(bugData);
    }
  };

  const disableSubmit =
    bugData.issueDescription === '' || !bugData.issueType || testBody;

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (disableSubmit) return;

    setDialogOpen(false);
    updateBugData(emptyBugData);

    if (pageData) {
      pageData.reportBug(bugData);
    }

    event.preventDefault();
  };
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateBugData({ ...bugData, [event.target.name]: event.target.value });
  };

  const issueTypeMenuItems = () => {
    const issueTypes = Object.values(BugType);
    const ret: JSX.Element[] = [];

    issueTypes.forEach((v, i): void => {
      ret.push(
        <MenuItem value={v} key={i}>
          {v}
        </MenuItem>
      );
    });

    return ret;
  };
  const onChangeIssueType = (event: SelectChangeEvent<string>): void => {
    const issueType = event.target.value as string;

    updateBugData({ ...bugData, issueType: issueType as BugType });
  };

  const onClose = () => {
    setDialogOpen(false);
  };

  const title = testBody ? 'Issue' : 'Report Issue';

  return (
    <>
      <Dialog open={dialogOpen} onClose={onClose} maxWidth={false}>
        <StyledForm onSubmit={onSubmit}>
          <Stack sx={stackProps}>
            <CloseButton onClick={onClose}>
              <IconClose color={'black'} height={'1.5em'} width={'1.5em'} />
            </CloseButton>

            <FormLabel sx={{ fontSize: 'large', fontWeight: 'bold' }}>
              <Centered>{title}</Centered>
            </FormLabel>
            {testBody && pageData?.scenario?.id ? (
              <Stack direction={'row'}>
                <FormLabel>Scenario: </FormLabel>
                <FormLabel>{pageData.scenario.id}</FormLabel>
              </Stack>
            ) : null}
            <FormControl fullWidth>
              {/* <InputLabel>Type of Issue</InputLabel> */}
              <Select
                fullWidth
                onChange={onChangeIssueType}
                displayEmpty
                value={bugData?.issueType || ''}
                renderValue={(value) => value || <div>Select type of issue <i>(required)</i></div>}
                disabled={testBody}
              >
                {issueTypeMenuItems()}
              </Select>
            </FormControl>
            <TextField
              name='issueDescription'
              label={<div>Issue Description <i>(required)</i></div>}
              rows={5}
              multiline
              value={bugData.issueDescription}
              onChange={onChange}
              fullWidth
            />
            <TextField
              fullWidth
              name='email'
              label={<div>Email <i>(optional)</i></div>}
              value={bugData?.email || ''}
              onChange={onChange}
              onKeyPress={(e) => {
                e.key === 'Enter' && e.preventDefault();
              }}
            />
            {testBody && bugData.platformInfo ? (
              <TextField
                name='platformInfo'
                label='Platform Information'
                rows={5}
                multiline
                value={JSON.stringify(bugData.platformInfo, null, 2)}
                onChange={onChange}
                fullWidth
              />
            ) : null}
            {!testBody ? (
              <Button
                sx={{
                  backgroundColor: !disableSubmit
                    ? 'var(--Action-Background)'
                    : 'var(--Action-Background-Disabled)',
                  color: 'var(--Action-Foreground)'
                }}
                disabled={disableSubmit}
                type='submit'
              >
                Submit
              </Button>
            ) : null}
          </Stack>
        </StyledForm>
      </Dialog>
      {(
        <Tooltip
          title={
            testBody
              ? 'Look at issue report'
              : 'Report any issues (typos, answer disagreement, technical issues) you encounter while using this app.'
          }
        >
          <>
            <IconButton
              sx={{ position: 'relative', right: '-.2em' }}
              onClick={() => setDialogOpen(true)}
            >
              <IconFlag />
            </IconButton>
          </>
        </Tooltip>
      )}
    </>
  );
};
